// PRC-023 · E1 (Mi suscripción) y E3 (cancelada, aún vigente). Las acciones abren el portal de Stripe.
import React, { useState } from 'react';
import { Box } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../../../context/AuthContext';
import { Eyebrow, JourneyButton, JourneyCard, JourneyChip, JourneyText } from './JourneyBasics';
import { Notice, TopBanner } from './JourneyBlocks';
import { J } from '../journeyTokens';
import {
  CURRENT_SUBSCRIPTION_QUERY_KEY, DATA_RETENTION_DAYS, fetchCurrentSubscription, formatLongDate, formatMoney,
  formatShortDate, intervalLabel, openBillingPortal, PLAN_LABEL,
} from '../journeyBilling';

export const useCurrentSubscription = (enabled = true) =>
  useQuery({
    queryKey: CURRENT_SUBSCRIPTION_QUERY_KEY,
    queryFn: fetchCurrentSubscription,
    enabled,
    staleTime: 5 * 60_000,
    retry: false,
  });

const addDays = (iso: string, days: number) => new Date(new Date(iso).getTime() + days * 86_400_000).toISOString();

const KV: React.FC<{ k: string; v: React.ReactNode }> = ({ k, v }) => (
  <Box>
    <Box sx={{ fontSize: 12, fontWeight: 700, letterSpacing: '.12em', textTransform: 'uppercase', color: J.muted }}>{k}</Box>
    <Box sx={{ fontSize: 15, fontWeight: 700, color: J.text, mt: '4px' }}>{v || '—'}</Box>
  </Box>
);

const usePortal = () => {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const open = async () => {
    setBusy(true);
    setError(null);
    try {
      await openBillingPortal();
    } catch {
      setBusy(false);
      setError('No se pudo abrir el portal de facturación. Inténtalo de nuevo.');
    }
  };
  return { busy, error, open };
};

// Banda ámbar de E3 en las pantallas privadas, solo para el administrador.
export const CancelledSubscriptionBanner: React.FC = () => {
  const { isPricingAdmin, pricingAccess } = useAuth();
  const enabled = isPricingAdmin && pricingAccess?.state === 'paid';
  const { data } = useCurrentSubscription(enabled);
  const portal = usePortal();
  if (!enabled || !data?.has_subscription || !data.cancel_at_period_end || !data.current_period_end) return null;
  return (
    <TopBanner
      tone="amber"
      title="Has cancelado tu suscripción"
      details={[`Sigues teniendo acceso completo hasta el ${formatLongDate(data.current_period_end)}. Después, el acceso se pausa y tus datos se guardan ${DATA_RETENTION_DAYS} días.`]}
      action={<JourneyButton kind="ghost" small disabled={portal.busy} onClick={portal.open}>Mantener suscripción</JourneyButton>}
    />
  );
};

const MySubscriptionPanel: React.FC = () => {
  const { data, isLoading, isError } = useCurrentSubscription();
  const portal = usePortal();

  if (isLoading) return <JourneyText variant="small">Cargando tu suscripción…</JourneyText>;
  if (isError || !data?.has_subscription) {
    return <Notice tone="danger">No hemos podido leer tu suscripción. <a href="#" onClick={(e) => { e.preventDefault(); void portal.open(); }}>Abrir el portal de facturación</a>.</Notice>;
  }

  const plan = data.plan ? PLAN_LABEL[data.plan] : 'PROA Pricing';
  const period = intervalLabel(data.interval);
  const cancelled = !!data.cancel_at_period_end;
  const per = data.interval === 'year' ? 'al año' : 'al mes';

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, fontFamily: J.font }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
        <Eyebrow>Mi suscripción</Eyebrow>
        <JourneyText variant="title">Plan {plan}</JourneyText>
      </Box>
      {portal.error && <Notice tone="danger">{portal.error}</Notice>}
      <JourneyCard sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', gap: '12px' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
            {cancelled
              ? <><JourneyChip tone="amber">Cancelada</JourneyChip><JourneyText variant="body" component="span">No se renovará. Acceso hasta el <b style={{ color: J.text }}>{formatLongDate(data.current_period_end)}</b>.</JourneyText></>
              : <><JourneyChip tone="ok">Activa</JourneyChip><JourneyText variant="body" component="span">Se renueva automáticamente el <b style={{ color: J.text }}>{formatLongDate(data.current_period_end)}</b>.</JourneyText></>}
          </Box>
          <JourneyButton small disabled={portal.busy} onClick={portal.open}>{cancelled ? 'Mantener suscripción' : 'Gestionar pago y facturas'}</JourneyButton>
        </Box>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, minmax(0,1fr))' }, gap: 2 }}>
          <KV k="Plan" v={[plan, period].filter(Boolean).join(' · ')} />
          {cancelled ? (
            <>
              <KV k="Último cargo" v={data.last_charge_at ? `${formatShortDate(data.last_charge_at)} · ${formatMoney(data.last_charge_amount, data.currency)} + IVA` : ''} />
              <KV k="Datos guardados hasta" v={data.current_period_end ? formatShortDate(addDays(data.current_period_end, DATA_RETENTION_DAYS)) : ''} />
            </>
          ) : (
            <>
              <KV k="Importe" v={`${formatMoney(data.amount, data.currency)} + IVA ${per}`} />
              <KV k="Método de pago" v={data.card_last4 ? `Tarjeta acabada en ${data.card_last4}` : ''} />
              <KV k="Usuarios" v={data.users_used != null && data.users_max != null ? `${data.users_used} de ${data.users_max}` : ''} />
              <KV k="Facturar a" v={data.billing_name} />
              <KV k="Cliente desde" v={formatShortDate(data.started_at)} />
            </>
          )}
        </Box>
      </JourneyCard>
      {!cancelled && (
        <>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, minmax(0,1fr))' }, gap: 2 }}>
            {[
              { t: 'Facturas', d: 'Descarga tus facturas con NIF e IVA desglosado.', l: 'Ver facturas' },
              { t: 'Cambiar de plan o periodo', d: 'Pasa a anual y ahorra dos meses, o habla con nosotros sobre Autonomy.', l: 'Cambiar plan' },
              { t: 'Cancelar', d: 'Sigues teniendo acceso hasta el final del periodo pagado.', l: 'Cancelar suscripción' },
            ].map((c) => (
              <JourneyCard key={c.t} tight sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                <JourneyText variant="h3">{c.t}</JourneyText>
                <JourneyText variant="small">{c.d}</JourneyText>
                <Box component="button" type="button" disabled={portal.busy} onClick={portal.open} sx={{ all: 'unset', cursor: 'pointer', color: J.skyText, fontWeight: 600, fontSize: 14 }}>{c.l}</Box>
              </JourneyCard>
            ))}
          </Box>
          <JourneyText variant="small">Las tres acciones abren el portal seguro de Stripe y vuelven aquí al terminar.</JourneyText>
        </>
      )}
    </Box>
  );
};

export default MySubscriptionPanel;
